import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useSession } from "./hooks/useSession";
import { isElectronRuntime } from "./lib/runtime";
import { SessionExpiredDialog } from "./components/SessionExpiredDialog";
import { ToastHost } from "./components/ToastHost";

// 로그인·랜딩 방문자에게 에디터 전체를 먼저 내려보내지 않는다. 특히 Firebase는
// 로그인 화면에서만, Prism과 편집 도구는 작업 공간에서만 필요하다.
const LandingPage = lazy(() =>
  import("./pages/LandingPage").then((module) => ({
    default: module.LandingPage,
  })),
);
const LoginPage = lazy(() =>
  import("./pages/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const WorkspacePage = lazy(() =>
  import("./pages/WorkspacePage").then((module) => ({
    default: module.WorkspacePage,
  })),
);

/**
 * 라우팅과 인증 가드.
 *
 * 미로그인 사용자는 어떤 경로로 들어와도 /login 으로,
 * 로그인한 사용자가 /login 에 들어오면 메인으로 보낸다.
 *
 * 작업 중에 세션이 끊기면(`session.expired`) 곧바로 튕겨내지 않고 먼저 알린다 —
 * 편집하던 화면이 왜 사라졌는지 모른 채 로그인 화면을 마주하는 것이 가장 나쁘다.
 */
export function App() {
  const session = useSession();
  // 지역 변수로 꺼내야 아래 JSX 에서 status 로 user 가 좁혀진다.
  const { state } = session;

  if (state.status === "loading") {
    return (
      <main className="centered">
        <p className="muted">확인 중…</p>
      </main>
    );
  }

  return (
    <>
      {/* 알림은 한 곳에서만 그린다. 여러 곳에 두면 같은 알림이 여러 번 뜬다. */}
      <ToastHost />

      {session.expired && (
        <SessionExpiredDialog
          onGoToLogin={session.goToLogin}
          onDismiss={session.dismissExpiry}
        />
      )}

      <Suspense
        fallback={
          <main className="centered">
            <p className="muted">화면 불러오는 중…</p>
          </main>
        }
      >
        <Routes>
          <Route
            path="/login"
            element={
              state.status === "authenticated" ? (
                <Navigate to="/" replace />
              ) : (
                <LoginPage
                  config={session.config}
                  configError={session.configError}
                  onLoggedIn={session.refresh}
                />
              )
            }
          />

          {state.status === "authenticated" ? (
            <>
              <Route
                path="/"
                element={
                  <WorkspacePage user={state.user} onLogout={session.logout} />
                }
              />
              <Route
                path="/d/:id"
                element={
                  <WorkspacePage user={state.user} onLogout={session.logout} />
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <>
              {/*
                브라우저의 미로그인 첫 화면은 로그인이 아니라 소개다. 이 서비스가
                무엇인지 모르는 사람에게 로그인 화면을 먼저 보이면 로그인할 이유가
                없다.

                데스크탑은 반대다. 앱을 내려받아 실행했다는 것 자체가 소개를 이미
                거쳤다는 뜻이고, 거기서 다시 "내려받으세요" 를 읽게 하는 것은
                가로막는 일이다. 그래서 바로 로그인으로 보낸다.
              */}
              <Route
                path="/"
                element={
                  isElectronRuntime() ? (
                    <Navigate to="/login" replace />
                  ) : (
                    <LandingPage />
                  )
                }
              />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          )}
        </Routes>
      </Suspense>
    </>
  );
}
